import React from "react";
import first from "lodash/first";

import { Box, Typography } from "@mui/material";

import {
  EditButton,
  PassengerContact,
  PassengerCount,
  LuggageCount,
  TripNotes,
} from "./components";
import {
  useScreenSize,
  useLuggage,
  useLaunchDarklyFlags,
  useCurrentUser,
  useDefaultPassenger,
} from "globals/hooks";
import { Contact, TemporaryPassenger } from "types";
import PassengerDetailsContact from "./components/PassengerDetailsContact";
import { SelectedPassenger } from "globals/hooks/useDefaultPassenger";

type AdditionalTripInfoBlockProps = {
  mode: "create" | "update";
  trip: {
    id: string;
    totalGroupSize: number;
    routes: {
      id: string;
      carryOnLuggage?: number;
      checkedLuggage?: number;
      oversizeLuggage?: number;
    }[];
    childSeats: {
      boosterSeatQuantity: number;
      forwardFacingSeatQuantity: number;
      rearFacingSeatQuantity: number;
    };
    note?: string;
    temporaryPassenger?: TemporaryPassenger;
    selectedPassenger?: SelectedPassenger;
    contact?: Contact;
  };
  passenger: {
    firstName: string;
    lastName: string;
    mobilePhone: string;
    phoneCountryCode?: string;
    phoneCountryDialCode?: string;
  };
  isRoundTrip?: boolean;
};

function AdditionalTripInfoBlock(props: AdditionalTripInfoBlockProps) {
  const { mode, trip, passenger, isRoundTrip } = props;

  // derived state
  const route = first(trip.routes);

  // hooks
  const { isMobileView } = useScreenSize();
  const { isLuggageEnabled } = useLuggage(route);
  const currentUser = useCurrentUser();
  const { enableLinkedPassenger } = useLaunchDarklyFlags();
  const { selectedPassenger } = useDefaultPassenger() || {};
  const renderSection = (children: React.ReactNode) =>
    isMobileView ? (
      <Box width="100%" display="flex" alignItems="flex-start">
        <Box flex={1} minWidth={0}>
          {children}
        </Box>
        <Box ml={1} flexShrink={0}>
          <EditButton
            compact
            mode={mode}
            trip={trip}
            isRoundTrip={isRoundTrip}
          />
        </Box>
      </Box>
    ) : (
      children
    );

  return (
    <Box display="flex" flexDirection="column" width="100%">
      {/* header */}
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h3">Additional Trip Info</Typography>
        {!isMobileView && (
          <EditButton mode={mode} trip={trip} isRoundTrip={isRoundTrip} />
        )}
      </Box>

      {/* body */}
      <Box
        mt={2}
        display="flex"
        flexDirection={isMobileView ? "column" : "row"}
        justifyContent="space-between"
      >
        {renderSection(<PassengerCount totalGroupSize={trip.totalGroupSize} />)}
        {renderSection(
          enableLinkedPassenger && currentUser ? (
            <PassengerDetailsContact
              temporaryPassenger={trip.temporaryPassenger}
              passenger={
                trip.contact || trip.selectedPassenger || selectedPassenger
              }
            />
          ) : (
            <PassengerContact
              temporaryPassenger={trip.temporaryPassenger}
              passenger={passenger}
            />
          )
        )}

        {isLuggageEnabled && renderSection(<LuggageCount {...route} />)}
        {renderSection(
          <TripNotes note={trip.note} childSeats={trip.childSeats} />
        )}
      </Box>
    </Box>
  );
}

export default AdditionalTripInfoBlock;
